import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, Check, Layers, Plus, X } from 'lucide-react';

interface Zone {
  id: string;
  name: string;
  color: string;
}

interface StepZonesProps {
  onComplete: (data: { zones: Array<{ name: string; color: string }> }) => void;
  onSkip: () => void;
}

const ZONE_COLORS = [
  '#3b82f6', // Bleu
  '#10b981', // Vert
  '#f59e0b', // Orange
  '#8b5cf6', // Violet
  '#ef4444', // Rouge
  '#ec4899', // Rose
];

const SUGGESTED_ZONES = [
  'Salle principale',
  'Terrasse',
  'Salle secondaire',
];

export function StepZones({ onComplete, onSkip }: StepZonesProps) {
  const [zones, setZones] = useState<Zone[]>([]);
  const [newZoneName, setNewZoneName] = useState('');

  const generateId = () => Math.random().toString(36).substring(2, 9);

  const getNextColor = () => {
    const usedColors = zones.map(z => z.color);
    const availableColor = ZONE_COLORS.find(c => !usedColors.includes(c));
    return availableColor || ZONE_COLORS[zones.length % ZONE_COLORS.length];
  };

  const handleAddZone = () => {
    if (!newZoneName.trim()) return;
    if (zones.some(z => z.name.toLowerCase() === newZoneName.trim().toLowerCase())) return;
    
    const newZone: Zone = {
      id: generateId(),
      name: newZoneName.trim(),
      color: getNextColor(),
    };
    
    setZones([...zones, newZone]);
    setNewZoneName('');
  };

  const handleRemoveZone = (id: string) => {
    setZones(zones.filter(z => z.id !== id));
  };

  const handleToggleSuggestion = (suggestion: string) => {
    const existingZone = zones.find(z => z.name.toLowerCase() === suggestion.toLowerCase());
    
    if (existingZone) {
      handleRemoveZone(existingZone.id);
    } else {
      const newZone: Zone = {
        id: generateId(),
        name: suggestion,
        color: getNextColor(),
      };
      setZones([...zones, newZone]);
    }
  };

  const handleSubmit = () => {
    onComplete({
      zones: zones.map(z => ({ name: z.name, color: z.color })),
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddZone();
    }
  };

  const isZoneSelected = (suggestion: string) => 
    zones.some(z => z.name.toLowerCase() === suggestion.toLowerCase());

  const customZones = zones.filter(
    z => !SUGGESTED_ZONES.some(s => s.toLowerCase() === z.name.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
          <Layers className="w-6 h-6 text-primary" />
        </div>
        <h2 className="text-xl font-semibold">Vos zones</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Définissez les zones de votre établissement
        </p>
      </div>

      {/* Suggestions - Main selection */}
      <div className="space-y-3">
        <Label>Sélectionnez vos zones :</Label>
        <div className="grid grid-cols-1 gap-2">
          {SUGGESTED_ZONES.map((suggestion) => {
            const isSelected = isZoneSelected(suggestion);
            return (
              <button
                key={suggestion}
                type="button"
                onClick={() => handleToggleSuggestion(suggestion)}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  isSelected 
                    ? 'border-primary bg-primary/10' 
                    : 'border-border hover:border-primary/50 bg-background'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{suggestion}</span>
                  {isSelected && <Check className="w-5 h-5 text-primary" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom zones list */}
      {customZones.length > 0 && (
        <div className="space-y-2">
          <Label className="text-muted-foreground text-sm">Zones personnalisées</Label>
          <div className="bg-muted/30 rounded-lg p-3 space-y-2">
            {customZones.map((zone) => (
              <div
                key={zone.id}
                className="flex items-center justify-between bg-background rounded-md p-2 border"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-4 h-4 rounded-full shrink-0"
                    style={{ backgroundColor: zone.color }}
                  />
                  <span className="text-sm font-medium">{zone.name}</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveZone(zone.id)}
                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Custom zone input - More discreet at the bottom */}
      <div className="space-y-2 pt-4 border-t">
        <Label className="text-muted-foreground text-sm">Autre zone (optionnel)</Label>
        <div className="flex gap-2">
          <Input
            value={newZoneName}
            onChange={(e) => setNewZoneName(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ex: Mezzanine, Jardin..."
            className="flex-1"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleAddZone}
            disabled={!newZoneName.trim()}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <Button variant="ghost" onClick={onSkip} className="flex-1">
          Passer
        </Button>
        <Button onClick={handleSubmit} className="flex-1">
          Continuer
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
